import React, { useEffect, useState } from 'react';
import { aopApi, agentForWorkflow, agentLabel, formatDateTime, workflowLabel, platformForWorkflow, platformLabel } from '../aopApi';

const TYPE_LABELS = {
  'orchestrator-daily-brief': 'Daily Brief',
  'orchestrator-weekly-brief': 'Weekly Brief',
  'orchestrator-opp-check': 'Hourly Opp Check',
  'copy-package': 'Copy Package',
  'design-packet': 'Design Packet',
  'dev-packet': 'Development Packet',
  'pm-queue-monitor': 'Queue Monitor',
  'pm-opp-shepherd': 'Opportunity Shepherd',
  'analytics-report': 'Analytics Report',
  'process-review': 'Process Review',
};

// Briefs have a readable shape; everything else renders as formatted JSON.
function ReportContent({ content }) {
  if (content && typeof content === 'object' && typeof content.summary === 'string') {
    return (
      <div className="aop-report-content">
        {content.summary}
        {Array.isArray(content.focus) && content.focus.length > 0 && (
          <>
            {'\n\nFOCUS:\n'}
            {content.focus.map((f) => `— ${f}`).join('\n')}
          </>
        )}
        {Array.isArray(content.risks) && content.risks.length > 0 && (
          <>
            {'\n\nRISKS:\n'}
            {content.risks.map((r) => `— [${String(r.severity || '').toUpperCase()}] ${r.detail || r.kind}`).join('\n')}
          </>
        )}
      </div>
    );
  }
  return (
    <pre className="aop-report-content" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
      {JSON.stringify(content, null, 2)}
    </pre>
  );
}

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [filterPlatform, setFilterPlatform] = useState('all');

  useEffect(() => {
    aopApi
      .reports({ limit: 50 })
      .then((res) => {
        if (!res.success) throw new Error(res.error || 'Failed to load reports');
        setReports(res.reports || []);
        if (res.reports && res.reports.length) setExpanded(res.reports[0]._id);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="aop-page-header">
        <div>
          <h1 className="aop-page-title">Reports</h1>
          <p className="aop-page-subtitle">Agent-generated briefs, copy packages, COP intelligence, and analysis — newest first</p>
        </div>
        <div className="aop-page-actions">
          <select className="aop-pill" value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)} style={{ width: 'auto', paddingRight: 28 }}>
            <option value="all">All Platforms</option>
            <option value="aop">AOP</option>
            <option value="cop">COP</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,98,93,0.1)', border: '1px solid rgba(255,98,93,0.25)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="aop-empty">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="aop-empty">
          <div className="aop-empty-icon">◎</div>
          No reports yet — run a workflow to generate one
        </div>
      ) : reports.filter((rep) => filterPlatform === 'all' || platformForWorkflow(rep.type) === filterPlatform).map((rep) => {
        const agent = agentForWorkflow(rep.type);
        const platform = platformForWorkflow(rep.type);
        const metrics = [
          { label: 'Confidence', value: rep.confidence != null ? `${Math.round(rep.confidence * 100)}%` : '—', color: 'var(--green)' },
          { label: 'Pending Actions', value: (rep.pendingActionIds || []).length, color: 'var(--orange)' },
          { label: 'Type', value: TYPE_LABELS[rep.type] || rep.type, color: 'var(--blue)' },
          { label: 'Run', value: rep.workflowRunId ? rep.workflowRunId.slice(-8) : '—', color: 'var(--text)' },
        ];
        return (
          <div key={rep._id} className="aop-card aop-report-card">
            <div
              className="aop-card-header"
              onClick={() => setExpanded(expanded === rep._id ? null : rep._id)}
              style={{ cursor: 'pointer' }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span className={`aop-badge ${platform}`}>{platformLabel(platform)}</span>
                  <span className={`aop-agent-tag ${agent}`}>{agentLabel(agent)}</span>
                  <span className="aop-badge" style={{ background: 'rgba(156,163,197,0.1)', color: 'var(--text-sec)' }}>
                    {rep.title || TYPE_LABELS[rep.type] || workflowLabel(rep.type)}
                  </span>
                </div>
                <div className="aop-card-subtitle">{formatDateTime(rep.createdAt)}</div>
              </div>
              <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>
                {expanded === rep._id ? '▲' : '▼'}
              </span>
            </div>

            {expanded === rep._id && (
              <div className="aop-report-body">
                <div className="aop-report-metric-row">
                  {metrics.map((m) => (
                    <div key={m.label} className="aop-report-metric">
                      <div className="aop-report-metric-val" style={{ color: m.color }}>{m.value}</div>
                      <div className="aop-report-metric-lbl">{m.label}</div>
                    </div>
                  ))}
                </div>
                <ReportContent content={rep.content} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
